import { tokyoYear, toWareki } from "../datetime.ts";
import type { SensitivityRules } from "../review-due.ts";
import { COMMON_RULES } from "./common.ts";
import type { ProfessionPack } from "./types.ts";

const yearLabel = (y: number) => `${y}年（${toWareki(y)}）`;

/** What in a legal article may change with the law: amounts, requirements, time limits, dates, reforms. */
const LAW_SENSITIVITY: SensitivityRules = {
  amount: { label: "金額・上限額", pattern: /\d[\d,，.]*\s?(?:万円|億円|円|%|％)|慰謝料|賠償額|上限額|罰金/ },
  relief: { label: "制度・要件", pattern: /要件|義務化|義務|罰則|過料|対象(?:と|に)なる|制度/ },
  deadline: { label: "期限・時効", pattern: /期限|時効|除斥期間|期間内|(?:\d+|[一二三四五六七八九十]+)(?:か月|ヶ月|カ月|年)以内|月\d+日まで/ },
  year: { label: "時点の書き方", pattern: /令和\d+年|\d{4}年(?:時点|現在|\d+月)|施行日/ },
  reform: { label: "改正・施行の記述", pattern: /改正|施行|義務化|予定|見込み|新設|廃止|創設/ },
};

/**
 * 弁護士. The expression rules follow the Japan Federation of Bar
 * Associations' rules on lawyers' advertising (弁護士の業務広告に関する規程)
 * and their guidance: no promised outcomes, no win rates, no comparison with
 * other lawyers, and 「専門」 avoided because there is no accreditation for it.
 */
export const LAWYER: ProfessionPack = {
  id: "lawyer",
  label: "弁護士",
  supervisorTitle: "弁護士",
  registrationLabel: "弁護士登録番号",
  reviewer: "弁護士の先生",
  writingRules: [
    "依頼主の法律事務所の立場で書く。文体は「です・ます」。一人称と表記は事務所の設定に従う。",
    "断定してよいのは、法令・裁判所や省庁の公表資料などの公的な一次資料に根拠がある場合だけ。根拠とした法令名・条文・資料名を出典として示す。",
    "法令は現行のものを書く。改正は施行日を明記し、施行前のものは「予定」と書く（例：「2027年4月1日から施行される予定です」）。",
    "期限・時効の期間・金額などの数字は、一次資料で確認できたものだけを書く。慰謝料などの相場は、根拠と幅を示せる場合だけ書き、事案によって変わると添える。",
    "判例に触れるのは、裁判所の公表資料などで確認できたものだけ。裁判所名と判決日を示す。",
    "弁護士の業務広告に関する規程に反する表現は使わない。結果の保証（「必ず勝てる」「確実に取り戻せる」など）、勝訴率・解決率、他の弁護士・事務所との比較、根拠のない最上級、「専門」の表示（「重点的に取り扱っている」と書く）、不安をあおる表現。",
    "個別の事件の見通しを断定しない。個別の判断が必要な内容は「ご相談ください」とし、CTA につなげる。",
    "相手方を一方的に非難したり、紛争をあおったりする書き方をしない。",
    "他のサイトの文章を写さない。競合の情報は構成の参考と、足りない点の把握にだけ使う。",
    "検索した人の知りたいことに冒頭で答える。必要に応じて FAQ を入れる。",
    "文字数稼ぎの水増しをしない。",
  ],
  expressionRules: [
    ...COMMON_RULES,
    {
      id: "law-outcome",
      pattern: /(必ず|確実に|絶対に?)(勝て|勝訴|勝ち取|取り戻せ|解決|減額|無罪|不起訴|認められ|免責)/g,
      severity: "high",
      category: "結果の保証",
      message: "事件の結果を保証する表現は、弁護士の業務広告に関する規程で認められていません",
      suggestion: "「〜が認められる可能性があります」とし、どのような事情で結果が変わるかを説明する",
    },
    {
      id: "law-win-rate",
      pattern: /勝訴率|解決率|成功率|勝率/g,
      severity: "high",
      category: "勝訴率などの表示",
      message: "勝訴率・解決率などの表示は、誤導・誤認のおそれがあるため控えるべきとされています",
      suggestion: "取り扱っている分野や手続きの流れを、事実として説明する",
    },
    {
      id: "law-comparison",
      pattern: /他の(弁護士|事務所|法律事務所)(より|と比べ|とは違|には(でき|ない))|どこよりも/g,
      severity: "high",
      category: "他の弁護士との比較",
      message: "他の弁護士・法律事務所との比較は、弁護士の業務広告に関する規程で認められていません",
      suggestion: "比較をやめ、当事務所の取り扱い方を事実として説明する",
    },
    {
      id: "law-specialist",
      pattern: /専門(の|と)(弁護士|する)|(相続|離婚|交通事故|債務整理|労働|刑事|企業法務)専門|スペシャリスト|エキスパート/g,
      severity: "medium",
      category: "「専門」の表示",
      message: "弁護士の「専門」には認定の制度がなく、表示は控えることが望ましいとされています",
      suggestion: "「〜を重点的に取り扱っています」「〜の取り扱いが多い」と書く",
    },
    {
      id: "law-individual",
      pattern: /あなたは.{0,20}(すべき|しなければなりません|勝て|認められます|請求できます)/g,
      severity: "medium",
      category: "個別の助言",
      message: "個別の事情によらず断定する助言になっています",
      suggestion: "「〜のような場合は、〜できる可能性があります。詳しくはご相談ください」にする",
    },
    {
      id: "law-future",
      pattern: /改正(され|になり)ます|(施行|適用)されます/g,
      severity: "low",
      category: "改正の書き方",
      message: "施行前の内容なら、施行日と「予定」を明記する必要があります",
      suggestion: "「〜年〜月〜日から施行される予定です（〇年〇月時点）」と書く",
    },
  ],
  trustedDomains: ["laws.e-gov.go.jp", "elaws.e-gov.go.jp", "courts.go.jp", "moj.go.jp", "houterasu.or.jp", "nichibenren.or.jp", "mhlw.go.jp", "caa.go.jp", "kokusen.go.jp", "npa.go.jp", "nta.go.jp", "mlit.go.jp"],
  authorityDomains: ["bengo4.com", "houterasu.or.jp", "nichibenren.or.jp", "vbest.jp", "adire.jp", "authense.jp", "nikkei.com", "ja.wikipedia.org"],
  specialtyOptions: ["相続・遺言", "離婚・男女問題", "交通事故", "債務整理・自己破産", "労働問題", "企業法務・契約書", "不動産・借地借家", "債権回収", "刑事事件", "消費者被害", "事業承継", "知的財産", "医療", "外国人・入管"],
  // 法律相談の暦: turning points of the year when people look up their problem.
  seasons: [
    { id: "nenmatsu-nenshi", name: "年末年始の帰省（相続・遺言の話し合い）", start: { month: 12, day: 25 }, end: { month: 1, day: 5 }, leadWeeks: 6, terms: ["相続", "遺言", "遺産分割", "相続放棄", "遺留分"], note: "家族が集まる年末年始は、相続や遺言の話題が出やすい時期です。" },
    { id: "keiyaku-koshin", name: "年度末の契約の見直し", start: { month: 2, day: 1 }, end: { month: 3, day: 31 }, leadWeeks: 6, terms: ["契約書", "賃貸借", "更新", "立ち退き", "取引基本契約"], note: "4月からの契約や賃貸の更新に向けて、契約書の見直しを調べる人が増えます。" },
    { id: "rikon", name: "新年度の前（離婚・別居）", start: { month: 1, day: 15 }, end: { month: 3, day: 31 }, leadWeeks: 8, terms: ["離婚", "別居", "親権", "養育費", "財産分与", "婚姻費用"], note: "子どもの進学や引っ越しの区切りに合わせて、離婚や別居の手続きを調べる人がいる時期です。" },
    { id: "taishoku", name: "退職・異動の時期（労働問題）", start: { month: 3, day: 1 }, end: { month: 4, day: 30 }, leadWeeks: 6, terms: ["退職", "残業代", "未払い", "解雇", "パワハラ", "退職代行"], note: "年度の区切りで退職や異動が増え、残業代や退職の手続きを調べる人が増えます。" },
    { id: "golden-week", name: "大型連休の行楽（交通事故）", start: { month: 4, day: 25 }, end: { month: 5, day: 10 }, leadWeeks: 6, terms: ["交通事故", "示談", "後遺障害", "過失割合", "慰謝料"], note: "行楽で車の利用が増える時期です。事故のあとの手続きを調べる人に向けて、前もって公開しておきます。" },
    { id: "kabunushi-sokai", name: "株主総会の時期（企業法務）", start: { month: 6, day: 1 }, end: { month: 6, day: 30 }, leadWeeks: 8, terms: ["株主総会", "取締役", "定款", "役員"], note: "3月決算の会社の株主総会が6月に集中し、手続きを調べる会社が増えます。" },
    { id: "obon", name: "お盆の帰省（相続の話し合い）", start: { month: 8, day: 1 }, end: { month: 8, day: 20 }, leadWeeks: 6, terms: ["相続", "遺言", "遺産分割", "成年後見", "家族信託"], note: "家族が集まり、相続や親の老後の話題が出やすい時期です。" },
    { id: "keiro", name: "敬老の日の前後（遺言・後見）", start: { month: 9, day: 10 }, end: { month: 9, day: 30 }, leadWeeks: 6, terms: ["遺言", "成年後見", "任意後見", "家族信託", "終活"], note: "親の老後や遺言について、家族で考えるきっかけになる時期です。" },
    { id: "nenmatsu-shiharai", name: "年末の支払い（借金の整理）", start: { month: 11, day: 15 }, end: { month: 12, day: 31 }, leadWeeks: 6, terms: ["債務整理", "自己破産", "任意整理", "個人再生", "借金"], note: "年末は支払いが重なり、借金の整理を考える人が調べる時期です。" },
  ],
  officeNoun: "法律事務所",
  officeExample: "山田法律事務所",
  affiliationExample: "大阪弁護士会",
  sourcesPhrase: "e-Gov 法令検索や裁判所などの公的な資料",
  sourceExamples: "e-Gov 法令検索・裁判所・法務省",
  keyFigures: "期限・時効の期間・金額",
  publishers: {
    "e-gov.go.jp": "e-Gov 法令検索",
    "courts.go.jp": "裁判所",
    "moj.go.jp": "法務省",
    "houterasu.or.jp": "法テラス（日本司法支援センター）",
    "nichibenren.or.jp": "日本弁護士連合会",
    "mhlw.go.jp": "厚生労働省",
    "caa.go.jp": "消費者庁",
    "kokusen.go.jp": "国民生活センター",
    "npa.go.jp": "警察庁",
    "nta.go.jp": "国税庁",
    "mlit.go.jp": "国土交通省",
  },
  period: {
    label: yearLabel,
    short: (y) => `${y}年`,
    rules: (y) => `${yearLabel(y)}時点の法令`,
    brief: (y) => `基準とする時点：${yearLabel(y)}時点の法令（現行の法令で書く。改正は施行日を明記し、施行前のものは「予定」と書く）`,
    mustState: false,
    current: tokyoYear,
  },
  review: { name: "法改正の見直し", when: "法改正の施行（4月1日が多い）や新しい年のあと", nowLabel: "現在", currentArticle: "最新の法令に合った記事", updateLabel: "法改正の反映", sensitivity: LAW_SENSITIVITY },
  features: { articles: true },
};
